import { useVisibility } from "@/contexts/VisibilityContext";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

const SAAS = [
  { key: "platforms.wesetu", name: "WeSetu", tagline: "Run your whole business in one place: leads, projects, invoices, contracts, and support." },
  { key: "platforms.urcard", name: "UrCard", tagline: "Your professional identity in a tap: smart digital business cards with QR, analytics, and more." },
  { key: "platforms.rxcircle", name: "RxCircle", tagline: "Your medical profile, ready when it matters most: tap or scan for instant critical details, plus full chronic-care management." },
  { key: "platforms.nexgen", name: "NexGen", tagline: "AI teammates that actually do the work: agents that run tasks, and a manager agent that runs the day." },
  { key: "platforms.mercuryflow", name: "MercuryFlow", tagline: "Point-of-sale that just works: live, and already serving paying businesses." },
  { key: "platforms.energy_intelligence", name: "Energy Intelligence", tagline: "See, understand, and cut your energy use." },
];

// A card's "effective" status. We can't claim LIVE if there's no destination yet.
const effectiveState = (v) => {
  if (v.state === "hidden") return "hidden";
  if (v.state === "coming_soon") return "coming_soon";
  if (v.state === "visible" && !v.external_url) return "preview"; // visible but no link → no false promise
  return "live";
};

const stateLabel = (eff) => ({
  live:        { tag: "LIVE",        color: "text-emerald-300" },
  preview:     { tag: "PREVIEW",     color: "text-white/40" },
  coming_soon: { tag: "COMING SOON", color: "text-amber-300" },
}[eff] || { tag: "PREVIEW", color: "text-white/40" });

export default function Platforms() {
  usePageTitle(
    "Platforms",
    "A constellation of SaaS apps we built to solve our own problems first — now yours. WeSetu, UrCard, RxCircle, NexGen, MercuryFlow, and Energy Intelligence."
  );
  const { get } = useVisibility();

  return (
    <div className="py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Products + Platforms</div>
        <h1 className="font-display text-4xl lg:text-6xl mt-3 tracking-tighter max-w-3xl">
          A constellation of <span className="gradient-text">SaaS apps</span>.
        </h1>
        <p className="mt-4 text-white/60 max-w-2xl">
          Software we built to solve our own problems first — now yours.
        </p>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SAAS.map((p) => {
            const v = get(p.key);
            const eff = effectiveState(v);
            if (eff === "hidden") return null;
            const sl = stateLabel(eff);
            const isLive = eff === "live";
            const isComing = eff === "coming_soon";
            const Card = (
              <div className={`glass rounded-2xl p-6 relative overflow-hidden transition-colors ${isLive ? "hover:border-white/25 cursor-pointer" : "opacity-90"}`}>
                <div className="flex items-center justify-between">
                  <div className={`text-[10px] uppercase tracking-widest font-mono ${sl.color}`}>{sl.tag}</div>
                  {isLive && <ArrowUpRight className="w-4 h-4 text-white/40" />}
                  {isComing && <Sparkles className="w-4 h-4 text-amber-300" />}
                </div>
                <h3 className="font-display text-2xl mt-5">{v.label || p.name}</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{v.teaser || p.tagline}</p>
                {isComing && (
                  <div className="mt-6 text-xs text-white/40 font-mono uppercase tracking-widest">Coming soon</div>
                )}
              </div>
            );
            return isLive ? (
              <a key={p.key} href={v.external_url} target="_blank" rel="noreferrer">{Card}</a>
            ) : (
              <div key={p.key}>{Card}</div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
